import { PortableTextBlock } from '@portabletext/react';
import { useContext, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Chevron, ShortArrow } from 'components/icons';
import { ButtonVariant } from 'components/rebrand/Button';
import { Toggle } from 'components/rebrand/Toggle';
import { P } from 'components/rebrand/Typography';
import { useTabsContext } from 'components/tabs/Tabs';

import { IQueryParams, ISanityCompareTier } from 'typescriptDefs';

import { useIsMobileSize } from 'helpers';
import formatTiersBenefits from 'helpers/formatTiersBenefits';

import { CheckmarkSVG } from '../icons/Checkmark';
import CustomPortableText from '../rebrand/CustomPortableText';
import { GetKOHOBtn } from '../rebrand/GetKOHOBtn';
import { LocaleContext } from '../rebrand/Layout';

const TierPrice = ({
  monthlyPrice,
  yearlyPrice,
  contextId = 'monthly-yearly-everything',
}: {
  monthlyPrice: PortableTextBlock | PortableTextBlock[];
  yearlyPrice: PortableTextBlock | PortableTextBlock[];
  contextId?: string;
}) => {
  const { activeTab } = useTabsContext(contextId);
  const currentPrice = activeTab === 0 ? monthlyPrice : yearlyPrice;

  return (
    <span className="ml-8 flex items-center [&>p]:text-sm [&>p]:font-medium [&>p]:lg:text-lg">
      <CustomPortableText content={currentPrice} />
    </span>
  );
};

const TabsToggle = ({
  contextId,
  label,
  labelClassName,
  activeToggleClassName,
}: {
  contextId: string;
  label: string;
  labelClassName: string;
  activeToggleClassName?: string;
}) => {
  const { activeTab, setActiveTab } = useTabsContext(contextId);

  const handleToggleChange = (checked: boolean) => {
    setActiveTab(checked ? 1 : 0);
  };

  return (
    <Toggle
      label={label}
      checked={activeTab === 1}
      labelClassName={labelClassName}
      activeToggleClassName={activeToggleClassName}
      onChange={handleToggleChange}
    />
  );
};

const ScrollProgress = ({ scrollProgress }: { scrollProgress: number }) => (
  <div className="h-3 w-full rounded-full bg-grey-75">
    <div
      className="h-full rounded-full bg-grey-200 transition-all duration-150 ease-out"
      style={{ width: `${scrollProgress}%` }}
    />
  </div>
);

const BenefitValue = ({
  value,
  textColorClass,
  isMobile,
}: {
  isMobile: boolean;
  textColorClass: string;
  value: string;
}) => {
  if (value === 'true') {
    return <CheckmarkSVG className={twMerge('[&_*]:stroke-[1.5px]', textColorClass, isMobile && 'text-grey-500')} />;
  } else {
    return <span className={isMobile ? 'text-grey-500' : textColorClass}>{value}</span>;
  }
};

const TiersTableContent = ({
  category,
  footerNotes,
  selectedTiers,
  tabLabelYearly,
  tableHeader,
  tiers,
  toggleBenefits,
  queryParams,
}: {
  category?: string;
  footerNotes?: PortableTextBlock | PortableTextBlock[];
  queryParams?: IQueryParams;
  selectedTiers: ISanityCompareTier[];
  tabLabelYearly: string;
  tableHeader: string;
  tiers: ISanityCompareTier[];
  toggleBenefits?: boolean;
}) => {
  const isMobile = useIsMobileSize();
  const locale = useContext(LocaleContext);
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});
  const [scrollProgress, setScrollProgress] = useState(0);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (tableContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
      const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;
      setScrollProgress(scrollPercentage);
    }
  };

  const toggleCategory = (categoryKey: string) => {
    setCollapsedCategories((prev) => ({
      ...prev,
      [categoryKey]: !prev[categoryKey],
    }));
  };

  const tierStyles = {
    essential: {
      column: 'bg-primary-50 md:border-x-4 md:border-x-white',
      title: 'text-primary-400',
      text: 'text-grey-500',
      cta: 'secondary' as ButtonVariant,
    },
    extra: {
      column: 'bg-primary-75 md:border-x-4 md:border-x-white',
      title: 'text-primary-400',
      text: 'text-grey-500',
      cta: 'secondary' as ButtonVariant,
    },
    everything: {
      column: 'bg-primary-400',
      title: 'text-white',
      text: 'text-white',
      cta: 'tertiaryOutline' as ButtonVariant,
    },
  };

  return (
    <>
      <div
        ref={tableContainerRef}
        onScroll={toggleBenefits ? handleScroll : undefined}
        className={twMerge(
          'w-full overflow-hidden rounded-16',
          toggleBenefits &&
            'max-h-[575px] overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        )}
      >
        <table className="h-full w-full table-fixed border-separate max-sm:border-spacing-x-4 sm:border-collapse">
          <colgroup>
            <col className="w-[26%] max-sm:w-2/3" />
            <col className="max-sm:w-1/3" />
          </colgroup>
          <thead className={twMerge(toggleBenefits && 'sticky top-0')}>
            <tr>
              {!isMobile && (
                <th className="bg-grey-50 text-left align-top">
                  <div className="flex h-full min-h-[200px] flex-col justify-between px-32 pt-24 shadow-sm">
                    <span className="text-4xl font-medium leading-128 text-grey-450">
                      {tableHeader}
                      <ShortArrow />
                    </span>

                    {toggleBenefits && <ScrollProgress scrollProgress={scrollProgress} />}
                  </div>
                </th>
              )}

              {selectedTiers.map((tier) => (
                <th
                  colSpan={!isMobile ? 1 : 2}
                  key={tier.variant}
                  className={twMerge(tierStyles[tier.variant].column, 'p-24 text-left max-sm:rounded-16')}
                >
                  <div className="flex h-full min-h-[160px] flex-col justify-between gap-16 sm:min-h-[200px] sm:gap-24">
                    <div>
                      <div className="flex items-center justify-between gap-4">
                        <span
                          className={twMerge(
                            tierStyles[tier.variant].title,
                            'flex items-center text-2xl font-medium leading-90 md:max-lg:text-xl',
                          )}
                        >
                          {tier.name}{' '}
                          {tier?.priceMonthly && tier?.priceYearly && (
                            <TierPrice
                              monthlyPrice={tier.priceMonthly}
                              yearlyPrice={tier.priceYearly}
                              contextId={
                                !isMobile
                                  ? 'monthly-yearly-desktop'
                                  : `monthly-yearly-${selectedTiers.length === 1 ? selectedTiers[0].variant : 'everything'}`
                              }
                            />
                          )}
                        </span>

                        {tier?.priceMonthly && tier?.priceYearly && isMobile && (
                          <TabsToggle
                            contextId={`monthly-yearly-${selectedTiers[0].variant}`}
                            label={tabLabelYearly}
                            labelClassName={tierStyles[tier.variant].title}
                            activeToggleClassName={twMerge(tier.variant === 'everything' && 'bg-primary-500')}
                          />
                        )}
                      </div>
                      <span className={twMerge(tierStyles[tier.variant].text, 'mt-4 block text-xs font-normal')}>
                        {tier.subtitle}
                      </span>
                    </div>

                    <span className={twMerge(tierStyles[tier.variant].text, 'text-xs font-normal')}>
                      {tier.description}
                    </span>

                    <GetKOHOBtn
                      buttonVariant={tierStyles[tier.variant].cta}
                      buttonClassName="w-full"
                      buttonLocation="Tiers Compare Table"
                      className="w-full sm:mt-16"
                      queryParams={{
                        hash: queryParams ? queryParams.hash : {},
                        search: {
                          af_adset_id: tier.btnAfAdsetId,
                          af_adset: tier.variant.toUpperCase(),
                          af_channel: 'PAGES',
                          ...queryParams?.search,
                        },
                      }}
                      trackEventProps={{
                        category: category,
                        label: 'Tiers Compare Table',
                        locale,
                        location: tier.btnAfAdsetId,
                      }}
                    >
                      {tier.btnText}
                    </GetKOHOBtn>
                  </div>
                </th>
              ))}
            </tr>

            {isMobile && (
              <tr>
                <td colSpan={2} className="sticky top-0 px-24 pt-4">
                  {toggleBenefits && <ScrollProgress scrollProgress={scrollProgress} />}
                </td>
              </tr>
            )}
          </thead>

          {formatTiersBenefits(tiers).map((category, index) => (
            <tbody key={category.categoryKey}>
              <tr>
                <td
                  className={twMerge(
                    'bg-grey-50 px-32 py-16',
                    toggleBenefits && 'cursor-pointer',
                    isMobile && index === 0 && 'rounded-tl-16',
                  )}
                  onClick={() => toggleBenefits && toggleCategory(category.categoryKey)}
                >
                  <div className="flex items-center justify-between">
                    <P className="font-medium text-grey-300">{category.categoryLabel}</P>
                    {toggleBenefits && (
                      <Chevron
                        className={twMerge(
                          'h-16 w-16 text-grey-300 transition-transform',
                          collapsedCategories[category.categoryKey] && 'rotate-180',
                        )}
                      />
                    )}
                  </div>
                </td>
                {selectedTiers.map((tier) => (
                  <td
                    key={tier.variant}
                    className={twMerge(
                      'p-24',
                      !isMobile ? tierStyles[tier.variant].column : 'bg-grey-50',
                      isMobile && index === 0 && 'rounded-tr-16',
                    )}
                  ></td>
                ))}
              </tr>

              {!collapsedCategories[category.categoryKey] &&
                category.benefits.map((benefit, index) => (
                  <tr key={index} className="group text-[14px]">
                    <td className="bg-grey-50 px-32 py-16 align-middle transition-shadow duration-300 ease-in-out group-hover:shadow-[inset_0_0_0_1000px_rgba(34,38,47,0.05)]">
                      <span className="font-medium text-grey-500">{benefit.name}</span>
                    </td>
                    {selectedTiers.map((tier) => (
                      <td
                        key={tier.variant}
                        className={twMerge(
                          'p-16 align-middle transition-shadow duration-300 ease-in-out group-hover:shadow-[inset_0_0_0_1000px_rgba(34,38,47,0.05)] md:px-32 md:py-16',
                          !isMobile ? tierStyles[tier.variant].column : 'bg-grey-50',
                          tier.variant === 'everything' && 'group-hover:shadow-[inset_0_0_0_1000px_rgba(0,0,0,0.10)]',
                        )}
                      >
                        <BenefitValue
                          isMobile={isMobile}
                          value={benefit[tier.variant]}
                          textColorClass={
                            tier.variant === 'everything' ? 'font-medium text-white' : 'font-medium text-primary-400'
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          ))}
        </table>
      </div>
      {footerNotes && (
        <div className="mt-16 text-grey-300 max-sm:px-18 [&_a]:text-primary-400">
          <CustomPortableText content={footerNotes} />
        </div>
      )}
    </>
  );
};

export default TiersTableContent;
